import cn from 'classnames';
import { Formik, Form, Field, FieldArray, ErrorMessage, getIn } from 'formik';
import { useState, useMemo, useContext } from 'react';
import * as Yup from 'yup';

import Button from 'components/common/Button';
import Text from 'components/common/Text';
import PlusSign from 'components/icons/signUp/PlusSign';
import { PickYourMealsContext } from 'components/signUpFunnel/Contexts';
import { TEMPO } from 'context/BrandContext';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useBrand from 'hooks/useBrand';
import { sendInvitation } from 'services/RequestService/endpoints/user';
import styles from 'styles/components/signUpFunnel/thankYou/InviteForm';

import { default as SmartphoneInviteForm } from './smartphone/InviteForm';

const InvitationEmailsSchema = Yup.object().shape({
  invitations: Yup.array()
    .of(
      Yup.object().shape({
        email: Yup.string().email('Invalid email'),
      })
    )
    .required('Must have an email')
    .min(1, 'Minimum of 1 email'),
});

const InviteForm = ({ brand, isSmartphoneScreenSize }) => {
  const isTempo = useBrand(TEMPO);
  const pathName = useMemo(() => window.location.pathname, [window.location]);
  const { isPickYourMeals } = useContext(PickYourMealsContext);

  const [invitationAlert, setInvitationAlert] = useState({
    showAlert: false,
    message: '',
    type: null,
  });

  const onSubmit = useAnalyticsCallback(
    (values, { setSubmitting, resetForm }) =>
      sendInvitation({ params: buildInvitationParams(values) }).then(response => {
        if (!response.ok) {
          setSubmitting(false);
          setInvitationAlert({
            showAlert: true,
            message: response.error,
            type: 'errorMessage',
          });
        } else {
          setSubmitting(false);
          resetForm({ emails: [''] });
          setInvitationAlert({
            showAlert: true,
            message: 'Thanks so much! We’ll send you an email when your friends sign up.',
            type: 'successMessage',
          });
        }
      }),
    ({ logEvent, events }) => {
      if (brand) {
        if (isPickYourMeals) {
          logEvent(events.pickMeals[TEMPO].thankYou.referralSent, {
            mealPlanSlug: TEMPO,
          });
        } else {
          logEvent(events.signUp[brand].thankYou.referralSent);
        }
      }
    },
    [brand, isPickYourMeals]
  );

  const InvitationAlert = () => {
    return (
      <Text
        className={cn('mx-auto w-full px-6 text-base leading-tight bp2:w-1/5', {
          'text-red-100': invitationAlert.type === 'errorMessage',
          'text-green-110': invitationAlert.type === 'successMessage' && !isTempo,
          'text-tempo-blue-90': invitationAlert.type === 'successMessage' && isTempo,
        })}
        brandStylesOverrides="textMd"
      >
        {invitationAlert.message}{' '}
      </Text>
    );
  };

  const buildInvitationParams = values => {
    return { emails: values.invitations.map(x => x.email), senderSource: pathName };
  };

  const DefaultInviteForm = ({ values, touched }) => {
    return (
      <Form aria-label="invite form">
        <FieldArray
          name="invitations"
          render={arrayHelpers => (
            <div>
              <div
                className={cn(
                  'flex flex-row flex-wrap text-center bp1:w-350',
                  styles.emailInputsWrapper
                )}
              >
                {values.invitations.map((email, index) => (
                  <div key={index}>
                    <label htmlFor={`invitations[${index}].email`} hidden>
                      Email Address
                    </label>
                    <Field
                      name={`invitations[${index}].email`}
                      placeholder="Email Address"
                      className={cn(styles.emailInput, 'textMd mt-2')}
                    />
                    {getIn(touched, `invitations[${index}].email`) && (
                      <ErrorMessage
                        name={`invitations[${index}].email`}
                        render={msg => <p className="errorMessage">{msg}</p>}
                      />
                    )}
                  </div>
                ))}
                <button
                  className={cn(styles.addButton, 'ml-4 mt-2 bp1:ml-6')}
                  type="button"
                  onClick={() => arrayHelpers.push({ email: '' })}
                >
                  <PlusSign />
                  <Text htmlElement="span" className="sr-only">
                    Add email address
                  </Text>
                </button>
              </div>
              <div>
                <Button type="submit" className="mx-auto mt-4 w-80 disabled:opacity-50 bp1:w-350">
                  Send Invitations
                </Button>
              </div>
            </div>
          )}
        />
      </Form>
    );
  };

  return (
    <div className="mt-4">
      {invitationAlert.showAlert && (
        <InvitationAlert message={invitationAlert.message} type={invitationAlert.type} />
      )}
      <Formik
        initialValues={{ invitations: [''] }}
        validationSchema={InvitationEmailsSchema}
        onSubmit={onSubmit}
      >
        {({ values, touched }) =>
          isSmartphoneScreenSize ? (
            <SmartphoneInviteForm values={values} touched={touched} />
          ) : (
            <DefaultInviteForm values={values} touched={touched} />
          )
        }
      </Formik>
    </div>
  );
};

InviteForm.propTypes = {
  brand: PropTypes.string,
  isSmartphoneScreenSize: PropTypes.bool,
  values: PropTypes.object,
  touched: PropTypes.object,
};

export default InviteForm;
